import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface VehicleAssignment {
  vehicle: any;
  adults: number;
  childrenAges: number[]; // Array con las edades específicas de los niños
}

export interface PassengerAssignment {
  adults: number;
  children: number[]; // Array original con todas las edades de niños
  remainingAdults: number;
  remainingChildren: number[]; // Array con edades de niños restantes
  selectedVehicles: VehicleAssignment[];
}

@Injectable({
  providedIn: 'root'
})
export class TransportsService {
  private readonly STORAGE_KEY = 'transport_assignment';

  private assignmentSubject = new BehaviorSubject<PassengerAssignment | null>(this.getStoredAssignment());
  public assignment$ = this.assignmentSubject.asObservable();

  initializeAssignment(paxList: any): void {
    const assignment: PassengerAssignment = {
      adults: paxList.adults,
      children: [...paxList.children],
      remainingAdults: paxList.adults,
      remainingChildren: [...paxList.children],
      selectedVehicles: []
    };

    this.saveAssignment(assignment);
    this.assignmentSubject.next(assignment);
  }

  canAssignVehicle(vehicle: any, componentSuggestion?: boolean): boolean {

    if(componentSuggestion) {
      return true;
    }

    const current = this.assignmentSubject.value;
    if (!current) return false;

    const remainingAdults = current.remainingAdults;
    const remainingChildren = current.remainingChildren.length;
    const maxPax = vehicle.vehicle.maxPax;

    // Debe haber al menos 1 adulto disponible
    if (remainingAdults < 1) return false;

    // Calcular cómo distribuir los pasajeros
    const possibleAssignments = this.calculatePossibleAssignments(
      remainingAdults,
      remainingChildren,
      maxPax
    );

    return possibleAssignments.length > 0;
  }

  assignVehicle(vehicle: any): boolean {
    const current = this.assignmentSubject.value;
    if (!current || !this.canAssignVehicle(vehicle)) return false;

    const remainingAdults = current.remainingAdults;
    const remainingChildren = current.remainingChildren;
    const maxPax = vehicle.vehicle.maxPax;

    // Encontrar la mejor asignación (priorizar adultos primero)
    const bestAssignment = this.findBestAssignment(remainingAdults, remainingChildren, maxPax);

    if (!bestAssignment) return false;

    // Crear el assignment del vehículo con las edades específicas
    const vehicleAssignment: VehicleAssignment = {
      vehicle: vehicle,
      adults: bestAssignment.adults,
      childrenAges: bestAssignment.childrenAges // Array con las edades específicas
    };

    // Actualizar assignment
    const newAssignment: PassengerAssignment = {
      ...current,
      remainingAdults: current.remainingAdults - bestAssignment.adults,
      remainingChildren: this.removeChildren(current.remainingChildren, bestAssignment.childrenAges),
      selectedVehicles: [...current.selectedVehicles, vehicleAssignment]
    };

    this.saveAssignment(newAssignment);
    this.assignmentSubject.next(newAssignment);
    return true;
  }

  removeVehicle(vehicle: any): void {

    const current = this.assignmentSubject.value;
    if (!current) return;

    const vehicleIndex = current.selectedVehicles
                        .findIndex(v => `${v.vehicle.bookParam}_${v.vehicle.payload[0]?.transfers.paxList.adults}_${v.vehicle.payload[0]?.transfers.paxList.children}` === `${vehicle.bookParam}_${vehicle.payload[0]?.transfers.paxList.adults}_${vehicle.payload[0]?.transfers.paxList.children}`);
    if (vehicleIndex === -1) return;

    const assignment = current.selectedVehicles[vehicleIndex];

    const newAssignment: PassengerAssignment = {
      ...current,
      remainingAdults: current.remainingAdults + assignment.adults,
      remainingChildren: this.addChildren(current.remainingChildren, assignment.childrenAges),
      selectedVehicles: current.selectedVehicles.filter((_, index) => index !== vehicleIndex)
    };

    this.saveAssignment(newAssignment);
    this.assignmentSubject.next(newAssignment);
  }

  private calculatePossibleAssignments(adults: number, childrenAges: any, maxPax: number): any[] {
    const assignments = [];
    const availableChildren = [...childrenAges];

    // Empezar desde 1 adulto (mínimo requerido) hasta el máximo posible
    for (let adultsToAssign = 1; adultsToAssign <= Math.min(adults, maxPax); adultsToAssign++) {
      const remainingCapacity = maxPax - adultsToAssign;
      const maxChildrenToAssign = Math.min(availableChildren.length, remainingCapacity);

      // Probar diferentes combinaciones de niños
      for (let childrenToAssign = 0; childrenToAssign <= maxChildrenToAssign; childrenToAssign++) {
        if (adultsToAssign + childrenToAssign <= maxPax && adultsToAssign >= 1) {
          // Tomar los primeros X niños disponibles
          const childrenAgesToAssign = availableChildren.slice(0, childrenToAssign);

          assignments.push({
            adults: adultsToAssign,
            children: childrenToAssign,
            childrenAges: childrenAgesToAssign // Array con edades específicas
          });
        }
      }
    }

    return assignments;
  }

  private findBestAssignment(adults: number, childrenAges: number[], maxPax: number): any {
    const assignments = this.calculatePossibleAssignments(adults, childrenAges, maxPax);

    if (assignments.length === 0) return null;

    // Priorizar asignaciones que usen más capacidad del vehículo
    return assignments.sort((a, b) => {
      const totalA = a.adults + a.children;
      const totalB = b.adults + b.children;
      return totalB - totalA; // Orden descendente por uso de capacidad
    })[0];
  }

  private removeChildren(current: number[], childrenToRemove: number[]): number[] {
    const result = [...current];

    // Remover las edades específicas del array
    childrenToRemove.forEach(age => {
      const index = result.indexOf(age);
      if (index > -1) {
        result.splice(index, 1);
      }
    });

    return result;
  }

  private addChildren(current: number[], childrenToAdd: number[]): number[] {
    // Agregar las edades específicas al array
    return [...current, ...childrenToAdd].sort((a, b) => a - b);
  }

  private saveAssignment(assignment: PassengerAssignment): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(assignment));
  }

  private getStoredAssignment(): PassengerAssignment | null {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  clearAssignment(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.assignmentSubject.next(null);
  }

  getRemainingPassengers(): { adults: number, children: number } {
    const current = this.assignmentSubject.value;
    return {
      adults: current?.remainingAdults || 0,
      children: current?.remainingChildren.length || 0
    };
  }

  isAssignmentComplete(): boolean {
    const current = this.assignmentSubject.value;
    return current ? current.remainingAdults === 0 && current.remainingChildren.length === 0 : false;
  }

  // Método helper para obtener el assignment actual
  getCurrentAssignment(): PassengerAssignment | null {
    return this.assignmentSubject.value;
  }

  // Método para debug - ver assignment detallado
  debugAssignment(): void {
    const current = this.getCurrentAssignment();
    if (current) {
      console.log('=== ASSIGNMENT DETALLADO ===');
      console.log('Total adultos:', current.adults);
      console.log('Total niños (edades):', current.children);
      console.log('Adultos restantes:', current.remainingAdults);
      console.log('Niños restantes (edades):', current.remainingChildren);
      console.log('Vehículos seleccionados:');

      current.selectedVehicles.forEach((vehicleAssignment, index) => {
        console.log(`  Vehículo ${index + 1}:`);
        console.log(`    - Adultos: ${vehicleAssignment.adults}`);
        console.log(`    - Niños (edades): ${vehicleAssignment.childrenAges}`);
        console.log(`    - Modelo: ${vehicleAssignment.vehicle.vehicle.name}`);
      });
    }
  }
}
